import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ─── localStorage read/unread tracking (mirrors SMRT Docs pattern) ──────────

const STORAGE_KEY = "profiles_notifications";

function getReadIds(): string[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? JSON.parse(raw) : [];
}

function saveReadIds(ids: string[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

function markIdsAsRead(ids: string[]): void {
  const existing = new Set(getReadIds());
  for (const id of ids) existing.add(id);
  saveReadIds([...existing]);
}

function isRead(id: string): boolean {
  return getReadIds().includes(id);
}

// ─── Traffic light helpers ──────────────────────────────────────────────────

type TrafficItem = {
  userId: string;
  userName: string;
  templateName: string;
  status: string;
  id?: string; // for green mark-as-read
};

export function getRedItems(
  users: any[],
  assignments: Record<string, string>,
): TrafficItem[] {
  return users
    .filter((u) => !assignments[u.user_id])
    .map((u) => ({
      userId: u.user_id,
      userName: u.name || u.email,
      templateName: "",
      status: "Unassigned",
    }));
}

export function getYellowItems(
  users: any[],
  assignments: Record<string, string>,
  submissions: any[],
  templates: { id?: string; name: string }[],
): TrafficItem[] {
  const subMap = new Map<string, any>();
  for (const s of submissions) subMap.set(`${s.userId}:${s.profileTemplateId}`, s);

  const tplMap = new Map<string, string>();
  for (const t of templates) if (t.id) tplMap.set(t.id, t.name);

  const items: TrafficItem[] = [];
  for (const u of users) {
    const tplId = assignments[u.user_id];
    if (!tplId) continue; // red, not yellow
    const sub = subMap.get(`${u.user_id}:${tplId}`);
    if (!sub || sub.status === "in_progress" || sub.status === "reopened") {
      items.push({
        userId: u.user_id,
        userName: u.name || u.email,
        templateName: tplMap.get(tplId) || "Profile",
        status: sub?.status === "reopened" ? "Reopened" : sub ? "In Progress" : "Not Started",
      });
    }
  }
  return items;
}

export function getGreenItems(
  users: any[],
  assignments: Record<string, string>,
  submissions: any[],
  templates: { id?: string; name: string }[],
): TrafficItem[] {
  const subMap = new Map<string, any>();
  for (const s of submissions) subMap.set(`${s.userId}:${s.profileTemplateId}`, s);

  const tplMap = new Map<string, string>();
  for (const t of templates) if (t.id) tplMap.set(t.id, t.name);

  const items: TrafficItem[] = [];
  for (const u of users) {
    const tplId = assignments[u.user_id];
    if (!tplId) continue;
    const sub = subMap.get(`${u.user_id}:${tplId}`);
    if (sub?.status === "submitted") {
      const greenId = `green:${u.user_id}:${tplId}`;
      if (!isRead(greenId)) {
        items.push({
          userId: u.user_id,
          userName: u.name || u.email,
          templateName: tplMap.get(tplId) || "Profile",
          status: "Ready for Review",
          id: greenId,
        });
      }
    }
  }
  return items;
}

// ─── Modal Component ────────────────────────────────────────────────────────

type Props = {
  isOpen: boolean;
  onClose: () => void;
  filter: "red" | "yellow" | "green" | null;
  users: any[];
  assignments: Record<string, string>;
  submissions: any[];
  templates: { id?: string; name: string }[];
};

const TITLES: Record<string, string> = {
  red: "Needs Attention",
  yellow: "Awaiting User Action",
  green: "Ready for Review",
};

const STATUS_COLORS: Record<string, string> = {
  red: "#c62828",
  yellow: "#d4a017",
  green: "#1faa59",
};

type GroupedUser = { userId: string; userName: string; items: { templateName: string; status: string; id?: string }[] };

function groupByUser(items: TrafficItem[]): GroupedUser[] {
  const map: Record<string, GroupedUser> = {};
  for (const item of items) {
    if (!map[item.userId]) {
      map[item.userId] = { userId: item.userId, userName: item.userName, items: [] };
    }
    map[item.userId].items.push({ templateName: item.templateName, status: item.status, id: item.id });
  }
  return Object.values(map);
}

export default function ProfilesTrafficModal({ isOpen, onClose, filter, users, assignments, submissions, templates }: Props) {
  const navigate = useNavigate();
  const [expandedUsers, setExpandedUsers] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [, setRefresh] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setRefresh((n) => n + 1);
      setSelectedIds([]);
    }
  }, [isOpen, filter]);

  if (!isOpen) return null;

  const title = filter ? TITLES[filter] : "All Notifications";
  const rawItems =
    filter === "red" ? getRedItems(users, assignments) :
    filter === "yellow" ? getYellowItems(users, assignments, submissions, templates) :
    filter === "green" ? getGreenItems(users, assignments, submissions, templates) :
    [];

  const groups = groupByUser(rawItems);
  const statusColor = STATUS_COLORS[filter || "red"] || "#9aa0a6";
  const isGreen = filter === "green";

  const allGreenIds: string[] = isGreen
    ? groups.flatMap((g) => g.items.filter((i) => i.id).map((i) => i.id!))
    : [];
  const allSelected = isGreen && allGreenIds.length > 0 && allGreenIds.every((id) => selectedIds.includes(id));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : [...allGreenIds]);
  };

  const handleMarkAsRead = () => {
    markIdsAsRead(selectedIds);
    setSelectedIds([]);
    setRefresh((n) => n + 1);
  };

  const handleViewAndMark = (group: GroupedUser) => {
    if (isGreen) {
      markIdsAsRead(group.items.filter((i) => i.id).map((i) => i.id!));
    }
    const tplId = assignments[group.userId];
    if (!tplId) return; // red — no template assigned, can't navigate
    onClose();
    navigate(`/profiles/view/${group.userId}/${tplId}`);
  };

  const toggleExpand = (userId: string) => {
    setExpandedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "550px", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>{title}</h2>
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>

        {isGreen && groups.length > 0 && (
          <div style={{ padding: "8px 20px", borderBottom: "1px solid #555", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#9aa0a6", fontSize: "0.8rem", cursor: "pointer" }}>
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ accentColor: "#1faa59" }} />
              Select All
            </label>
            {selectedIds.length > 0 && (
              <button
                className="btn-icon"
                style={{ background: "#1faa59", border: "none", borderRadius: 6, color: "#fff", padding: "6px 20px", fontSize: "0.82rem", cursor: "pointer", whiteSpace: "nowrap", width: "auto", minWidth: 130 }}
                onClick={handleMarkAsRead}
              >
                Mark As Read
              </button>
            )}
          </div>
        )}

        <div className="smrtdocs-modal-body" style={{ overflowY: "auto", flex: 1 }}>
          {groups.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {groups.map((group) => (
                <div key={group.userId}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      background: "#1c1c1c",
                      border: "1px solid #2a2a2a",
                      borderRadius: 6,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "#fff", fontSize: "0.95rem" }}>{group.userName}</div>
                      <div style={{ fontSize: "0.8rem", color: statusColor }}>
                        {group.items.length} item{group.items.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        className="btn-icon"
                        style={{ width: 32, height: 32, fontSize: 12, background: "#4b5563", border: "1px solid #6b7280", borderRadius: 6, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                        title={expandedUsers.includes(group.userId) ? "Collapse" : "Expand"}
                        onClick={() => toggleExpand(group.userId)}
                      >
                        {expandedUsers.includes(group.userId) ? "▶" : "▼"}
                      </button>
                      <button
                        className="btn-icon smrtdocs-btn-view"
                        style={{ width: 32, height: 32, fontSize: 14, flexShrink: 0 }}
                        title="View"
                        onClick={() => handleViewAndMark(group)}
                      >
                        👁
                      </button>
                    </div>
                  </div>

                  {expandedUsers.includes(group.userId) && (
                    <div style={{ paddingLeft: 12, paddingTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                      {group.items.map((item, j) => (
                        <div key={j} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.88rem", color: "#9aa0a6" }}>
                          {isGreen && item.id && (
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(item.id)}
                              onChange={() => toggleSelect(item.id!)}
                              style={{ accentColor: "#1faa59", cursor: "pointer" }}
                            />
                          )}
                          {filter === "red"
                            ? "Assign Profile Template"
                            : filter === "yellow"
                              ? item.status === "Reopened"
                                ? `${item.templateName} — reopened, awaiting resubmission`
                                : item.status === "In Progress"
                                  ? `${item.templateName} — started, not yet submitted`
                                  : "Waiting for Profile to be submitted"
                              : "Submitted for Review"}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#9aa0a6", textAlign: "center", padding: "20px 0" }}>
              No items
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
